import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import { feedPath } from '../listings/countries';
import apiService from '../../api';

const EMPTY = { platform: 'tiktok', url: '', name: '', whatsapp: '', area: '', email: '' };

// "Ajouter mon compte": an agent asks to have their TikTok or Facebook listings on Recouvro.
// The request is approved in the admin (listings/agents.py); nothing is collected before.
function AgentSignupPage() {
  const { country } = useParams();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('form'); // form | sending | pending | already
  const [errors, setErrors] = useState({});
  const [agentPath, setAgentPath] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrors({});
    try {
      const data = await apiService.post('/api/v1/listings/agent-requests/', { ...form, country });
      if (data.status === 'already') setAgentPath(data.path);
      setStatus(data.status === 'already' ? 'already' : 'pending');
    } catch (err) {
      const code = err?.response?.status;
      setErrors(code === 400 ? err.response.data : { form: code === 429 ? 'Trop de demandes. Réessayez dans une heure.' : "Impossible d'envoyer la demande. Réessayez." });
      setStatus('form');
    }
  };

  const input = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const error = (k) => errors[k] && <p className="text-sm text-red-600 mt-1">{[].concat(errors[k])[0]}</p>;
  const tiktok = form.platform === 'tiktok';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>Ajouter mes annonces sur Recouvro - Agents immobiliers</title>
        <meta name="description" content="Agent immobilier ? Vos annonces TikTok et Facebook sur Recouvro, gratuitement : plus de clients vous contactent directement sur WhatsApp." />
      </Helmet>
      <Header />
      <main className="flex-grow px-4 py-10 sm:py-14">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
          {status === 'pending' || status === 'already' ? (
            <div className="text-center space-y-3">
              <CheckCircle2 size={44} className="mx-auto text-green-600" />
              <h1 className="text-xl font-bold text-gray-900">{status === 'already' ? 'Vous êtes déjà sur Recouvro' : 'Demande envoyée'}</h1>
              <p className="text-sm text-gray-600">
                {status === 'already'
                  ? 'Vos annonces apparaissent déjà. Partagez votre page pour que vos clients voient toutes vos annonces au même endroit.'
                  : 'Merci ! Nous vérifions votre compte. Une fois validé, vos nouvelles annonces apparaîtront automatiquement sur Recouvro.'}
              </p>
              <Link to={agentPath || feedPath(country)} className="inline-block mt-2 bg-blue-600 text-white font-semibold rounded-lg px-5 py-2.5">
                {agentPath ? 'Voir ma page' : 'Voir les annonces'}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Ajouter mes annonces</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Agent immobilier ? Vos annonces TikTok ou Facebook apparaissent sur Recouvro, gratuitement.
                  Les clients vous contactent directement sur WhatsApp ou par téléphone : Recouvro ne prend aucune commission.
                </p>
              </div>

              <div className="grid grid-cols-2 border border-gray-300 rounded-lg overflow-hidden" role="group" aria-label="Plateforme">
                {[['tiktok', 'TikTok'], ['facebook', 'Facebook']].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, platform: v }))}
                    className={`py-2.5 text-sm ${form.platform === v ? 'bg-blue-600 text-white font-semibold' : 'bg-white text-gray-600'}`}>
                    {label}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tiktok ? 'Lien de votre compte TikTok' : 'Lien de votre page Facebook'}</label>
                <input required value={form.url} onChange={set('url')} className={input}
                  placeholder={tiktok ? 'https://www.tiktok.com/@votrecompte' : 'https://www.facebook.com/votrepage'} />
                <p className="text-xs text-gray-500 mt-1">{tiktok ? 'Le lien d\'une de vos vidéos marche aussi.' : 'Une page Facebook de préférence (les profils personnels ne sont pas encore pris en charge).'}</p>
                {error('url')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Votre nom ou celui de votre agence</label>
                <input required value={form.name} onChange={set('name')} className={input} placeholder="Ex. : Billy Immo" />
                {error('name')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Numéro WhatsApp des clients</label>
                <input value={form.whatsapp} onChange={set('whatsapp')} className={input} placeholder="01 97 45 73 86" inputMode="tel" />
                <p className="text-xs text-gray-500 mt-1">Le bouton WhatsApp de vos annonces utilisera ce numéro.</p>
                {error('whatsapp')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zones où vous travaillez <span className="text-gray-400">(facultatif)</span></label>
                <input value={form.area} onChange={set('area')} className={input} placeholder="Ex. : Abomey-Calavi, Cotonou" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-mail <span className="text-gray-400">(facultatif)</span></label>
                <input type="email" value={form.email} onChange={set('email')} className={input} placeholder="Pour vous prévenir quand c'est validé" />
                {error('email')}
              </div>
              {error('form')}
              <button type="submit" disabled={status === 'sending'} className="w-full bg-blue-600 text-white font-semibold rounded-lg py-3 disabled:opacity-50">
                {status === 'sending' ? 'Envoi…' : 'Envoyer ma demande'}
              </button>
              <p className="text-xs text-gray-500">Seules vos publications publiques sont reprises, avec un lien vers votre compte. Vous pouvez demander leur retrait à tout moment.</p>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default AgentSignupPage;
